<template>
  <div
    :class="blockClass">
    <div
      class="select-dropdown__current-option-container"
      :class="showDrawer ? 'highlight': '' "
      @click.prevent="toggleDropdown"
      v-if="currentOption">

      <input
        v-if="hiddenInputField"
        type="hidden"
        :value="currentOption.id"
        :name="hiddenInputField">

      <div
        class="select-dropdown__current-option">
        <component
          class="select-dropdown__option-icon"
          v-if="iconComponent(currentOption.icon)"
          :is="iconComponent(currentOption.icon)" />
        <div class="select-dropdown__current-option-name" v-html="currentOption.name" />
      </div>

      <LockIcon
        v-if="disabled"
        class="select-dropdown__lock-icon w-16 h-16" />
      <ChevronDown
        v-else
        class="select-dropdown__chevron"
        :style="{ transform: showDrawer ? 'rotate(180deg)' : '' }" />
    </div>

    <transition name="slide">
      <div class="select-dropdown__items" v-if="showDrawer">
        <div
          class="select-dropdown__item"
          v-for="option in availableOptions"
          :key="option.id"
          @click.prevent="selectOption(option.id)">
          <component
            class="select-dropdown__option-icon"
            v-if="iconComponent(option.icon)"
            :is="iconComponent(option.icon)" />
          <div class="select-dropdown__item-name" v-html="option.name" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// IMPORTANT: parent element should have position: relative;
import ChevronDown from "@images/font_awesome/chevron-down-light.svg";
import BarChartIcon from "@images/font_awesome/chart-bar-light.svg";
import PieChartIcon from "@images/font_awesome/chart-pie-light.svg";
import LineChartIcon from "@images/font_awesome/chart-line-light.svg";
import TableChartIcon from "@images/font_awesome/table-light.svg";
import DatabaseIcon from "@images/font_awesome/database-light.svg";
import LockIcon from "@images/font_awesome/lock-alt-light.svg";
import { bemClass } from "helpers/bem-class";

export default {
  components: {
    ChevronDown,
    BarChartIcon,
    PieChartIcon,
    LineChartIcon,
    TableChartIcon,
    DatabaseIcon,
    LockIcon
  },
  data() {
    return {
      currentOption: null,
      options: [],
      showDrawer: false
    }
  },
  props: {
    modifiers: {
      type: Array,
      default: () => []
    },
    inputOptions: {
      type: Array,
      default: []
    },
    hiddenInputField: {
      type: String
    },
    inputValue: {
      type: [Number, String]
    },
    windowStateKey: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    availableOptions() {
      return this.options;
    },
    allModifiers() {
      let result = []

      if (this.disabled) {
        result.push("disabled");
      }

      return result.concat(this.modifiers);
    },
    blockClass() {
      return bemClass({
        baseClass: "select-dropdown",
        modifiers: this.allModifiers
      });
    }
  },
  watch: {
    inputValue(newOptionId) {
      this.currentOption = this.options.find(o => {
        return o.id === newOptionId;
      });

    },
    currentOption(newOption) {
      if(this.windowStateKey && typeof(window.state) === "object") {
        window.state[this.windowStateKey] = newOption.id;
      }
    }
  },
  methods: {
    iconComponent(icon) {
      if(!icon) {
        return;
      }

      switch (icon) {
        case "font_awesome/chart-line-light.svg":
          return "LineChartIcon";
        case "font_awesome/chart-bar-light.svg":
          return "BarChartIcon";
        case "font_awesome/chart-pie-light.svg":
          return "PieChartIcon";
        case "font_awesome/database-light.svg":
          return "DatabaseIcon";
        case "font_awesome/table-light.svg":
          return "TableChartIcon";
        default:
          return null;
      }
    },
    toggleDropdown() {
      if (this.disabled) {
        return;
      }

      this.showDrawer = !this.showDrawer;
    },
    selectOption(optionId) {
      this.currentOption = this.options.find(o => {
        return o.id === optionId;
      });

      this.toggleDropdown();

      this.$emit("selected", this.currentOption.id);
    }
  },
  mounted() {
    this.inputOptions.forEach(o => {
      this.options.push({
        name: o[0],
        id: o[1],
        icon: o[2]
      })
    });

    if(this.inputValue) {
      this.currentOption = this.options.find(o => {
        return o.id == this.inputValue;
      })
    } else {
      this.currentOption = this.options[0];
    }
  }
}
</script>

<style lang="scss">
.slide-enter {
  opacity: 0;
  transform: translateY(-20px);
  transition: 0.15s all ease;
}

.slide-enter-to {
  opacity: 1;
  transform: translateY(0px);
  transition: 0.15s all ease;
}

.slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
  transition: 0.15s all ease;
}

.select-dropdown {
  $root: &;

  width: 100%;

  border: $px1 solid $grey-8;
  border-radius: $px4;

  background-color: $white;

  @include font-small;
  font-weight: 600;

  cursor: pointer;

  &__current-option-container {
    min-width: $px240;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $px8;
  }

  &--disabled {
    cursor: not-allowed;
  }

  &--small {
    @include font-micro;
    text-transform: none; // remove uppercase

    #{$root}__current-option-container {
      padding: $px4 $px8;
    }
  }

  &__current-option {
    display: flex;
    align-items: center;

    @include font-small;
  }

  &__current-option-name {
    display: flex;
    align-items: center;
  }

  &__lock-icon {
    color: $grey-8;
  }

  &__chevron {
    width: $px16;
    height: $px16;

    color: $grey-8;

    transition: 0.5s transform ease;
    transform-origin: 50%;
  }

  &__items {
    position: absolute;
    top: 40px;
    left: 0;
    z-index: 100;
    width: 100%;

    border: $px1 solid $grey-8;
    border-radius: $px4;

    background-color: $white;
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 9px $px8;

    @include font-small;

    &:first-child {
      border-radius: $px4 $px4 0 0;
    }

    &:last-child {
      border-radius: 0 0 $px4 $px4;
    }

    &:hover {
      background-color: $grey-10;
    }
  }

  &__item-name {
    display: flex;
    align-items: center;
  }

  &__option-icon {
    width: $px16;
    height: $px16;
    margin-right: $px4;
  }
}
</style>
